import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeTableModule } from 'primeng/treetable';

import { firstValueFrom } from 'rxjs';

import { SETTINGS, Settings } from '../../../../app.settings';

import { notifyAndTrackError } from '../../../../common/error';
import { successMsg } from '../../../../common/messages';
import { catalogWorkflowNotitificationSort } from '../../../../common/sorts';

import { AddressBookComponent, CanComponentDeactivate } from '../../../../components';

import { AppInsightsService } from '../../../../services/app-insights.service';
import { CatalogAdminService, ICatalogWorkflowNotification, ICatalogWorkflowNotificationUpdateRequest } from '../../../../services/insecta';

@Component({
  selector: 'tab-notification',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ContextMenuModule,
    CommonModule,
    DatePickerModule,
    DialogModule,
    EditorModule,
    FloatLabelModule,
    FieldsetModule,
    FormsModule,
    InputNumberModule,
    InputTextModule,
    RouterModule,
    SelectModule,
    TableModule,
    TabsModule,
    TextareaModule,
    ToggleSwitchModule,
    TreeSelectModule,
    TreeTableModule,
    AddressBookComponent
  ],

  templateUrl: 'tab-notifications.component.html'
})
export class TabNotificationComponent implements OnDestroy, OnInit, CanComponentDeactivate {
  dirtyConfirm$: ((leave: boolean) => void) | null = null;
  emptyGuid: string = `00000000-0000-0000-0000-000000000000`;

  pageVars: any = {
    isDirty: false,
    isEditing: false,
    isReady: false,
    pageId: `Admin:SiteCatalog#notifications`,
    showDirtyDlg: false
  };
  dlgVars: any = {
    notification: <ICatalogWorkflowNotification>{},
    visible: false,
    hdrEditorVisible: false,
    hdrEditorValue: ``,
    bdyEditorVisible: false,
    bdyEditorValue: ``,
    ftrEditorVisible: false,
    ftrEditorValue: ``
  };
  isNotificationsDialogValid: boolean = true;

  @Input() currentUser: string = ``;
  @Input() catalogId: string = this.emptyGuid;
  @Input() catalogWorkflowNotifications: ICatalogWorkflowNotification[] = [];
  @Output('isReady') isReady = new EventEmitter<boolean>();

  notificationId?: string;
  isAvailable: boolean = true;

  constructor(
    private appInsights: AppInsightsService,
    private catalogService: CatalogAdminService,
    private msgService: MessageService,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:SiteCatalog:NotificationsTab`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/sitecatalog#tabnotifications`);
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) return Promise.resolve(true);

    return new Promise((resolve) => {
      this.pageVars.showDirtyDlg = true;
      const confirm$ = (decision: boolean) => {
        this.dirtyConfirm$ = null;
        resolve(decision);
      };
      this.dirtyConfirm$ = confirm$;
    });
  }

  dirtyConfirm(leave: boolean) {
    this.pageVars.showDirtyDlg = false;
    if (this.dirtyConfirm$) {
      this.dirtyConfirm$(leave);
    }
  }

  ngOnDestroy() {
    this.pageVars = {
      isReady: false
    };
    this.catalogWorkflowNotifications = [];
  }

  ngOnInit() {
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);
    this.pageVars.isReady = true;
    this.pageVars.isEditing = false;
    this.isReady.emit(true);
  }

  updateCatalogWorkflowNotification(catalogNotification: ICatalogWorkflowNotification) {
    const request = <ICatalogWorkflowNotificationUpdateRequest>{
      id: catalogNotification.id,
      toList: catalogNotification.toList ?? [],
      ccList: catalogNotification.ccList ?? [],
      subjectTemplate: catalogNotification.subjectTemplate,
      bodyHeaderText: catalogNotification.bodyHeaderText,
      bodyTemplate: catalogNotification.bodyTemplate,
      bodyFooterText: catalogNotification.bodyFooterText,
      isActive: catalogNotification.isActive,
      actionBy: this.currentUser
    };

    firstValueFrom(this.catalogService.updateCatalogWorkflowNotification(catalogNotification.catalogId, request))
      .then(() => {
        const idx = this.catalogWorkflowNotifications.findIndex((x) => x.id === catalogNotification.id);
        this.catalogWorkflowNotifications[idx].action = 'UPD';
        this.catalogWorkflowNotifications[idx].actionBy = this.currentUser;
        this.catalogWorkflowNotifications[idx].actionOn = new Date();

        this.catalogWorkflowNotifications = this.catalogWorkflowNotifications.sort(catalogWorkflowNotitificationSort);

        successMsg(this.msgService, 'Catalog Notification has been updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: 'TabNotificationComponent',
          action: 'UpdateCatalogWorkflowNotification:Error'
        });
      });
  }

  dlgCancel() {
    this.dlgVars.visible = false;
    this.dlgVars.notification = null;
  }

  dlgEdit(notification: ICatalogWorkflowNotification) {
    this.dlgVars.notification = notification;
    this.dlgVars.visible = true;
  }

  dlgSave() {
    this.isReady.emit(false);

    const request = <ICatalogWorkflowNotificationUpdateRequest>{
      id: this.dlgVars.notification.id,
      toList: this.dlgVars.notification.toList ?? [],
      ccList: this.dlgVars.notification.ccList ?? [],
      subjectTemplate: this.dlgVars.notification.subjectTemplate,
      bodyHeaderText: this.dlgVars.notification.bodyHeaderText,
      bodyTemplate: this.dlgVars.notification.bodyTemplate,
      bodyFooterText: this.dlgVars.notification.bodyFooterText,
      isActive: this.dlgVars.notification.isActive,
      actionBy: this.currentUser
    };

    firstValueFrom(this.catalogService.updateCatalogWorkflowNotification(this.dlgVars.notification.catalogId, request))
      .then(() => {
        const idx = this.catalogWorkflowNotifications.findIndex((x) => x.id === request.id);
        if (idx >= 0) {
          this.catalogWorkflowNotifications[idx].toList = request.toList ?? [];
          this.catalogWorkflowNotifications[idx].ccList = request.ccList ?? [];
          this.catalogWorkflowNotifications[idx].subjectTemplate = request.subjectTemplate;
          this.catalogWorkflowNotifications[idx].bodyHeaderText = request.bodyHeaderText;
          this.catalogWorkflowNotifications[idx].bodyTemplate = request.bodyTemplate;
          this.catalogWorkflowNotifications[idx].bodyFooterText = request.bodyFooterText;
          this.catalogWorkflowNotifications[idx].isActive = request.isActive;
          this.catalogWorkflowNotifications[idx].action = 'UPD';
          this.catalogWorkflowNotifications[idx].actionBy = this.currentUser;
          this.catalogWorkflowNotifications[idx].actionOn = new Date();
        }

        this.catalogWorkflowNotifications = this.catalogWorkflowNotifications.sort(catalogWorkflowNotitificationSort);

        successMsg(this.msgService, 'Catalog Notification has been saved');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabNotificationComponent', action: 'Dialog:Save:Error' });
      })
      .finally(() => {
        this.isReady.emit(true);
        this.dlgVars.visible = false;
        this.computeNotificationsDialogValidation();
      });
  }

  // Removed legacy dlgIsValid wrapper; template should bind directly to isNotificationsDialogValid

  private computeNotificationsDialogValidation() {
    this.isNotificationsDialogValid = !(this.dlgVars.hdrEditorVisible || this.dlgVars.bdyEditorVisible || this.dlgVars.ftrEditorVisible);
    return this.isNotificationsDialogValid;
  }

  hdrEditorCancel() {
    this.dlgVars.hdrEditorValue = ``;
    this.dlgVars.hdrEditorVisible = false;
    this.computeNotificationsDialogValidation();
  }

  hdrEditorDone() {
    this.dlgVars.notification.bodyHeaderText = this.dlgVars.hdrEditorValue;
    this.dlgVars.hdrEditorValue = ``;
    this.dlgVars.hdrEditorVisible = false;
    this.computeNotificationsDialogValidation();
  }

  hdrEditorShow() {
    this.dlgVars.hdrEditorValue = this.dlgVars.notification.bodyHeaderText;
    this.dlgVars.hdrEditorVisible = true;
    this.computeNotificationsDialogValidation();
  }

  bdyEditorCancel() {
    this.dlgVars.bdyEditorValue = ``;
    this.dlgVars.bdyEditorVisible = false;
    this.computeNotificationsDialogValidation();
  }

  bdyEditorDone() {
    this.dlgVars.notification.bodyTemplate = this.dlgVars.bdyEditorValue;
    this.dlgVars.bdyEditorValue = ``;
    this.dlgVars.bdyEditorVisible = false;
    this.computeNotificationsDialogValidation();
  }

  bdyEditorShow() {
    this.dlgVars.bdyEditorValue = this.dlgVars.notification.bodyTemplate;
    this.dlgVars.bdyEditorVisible = true;
    this.computeNotificationsDialogValidation();
  }

  ftrEditorCancel() {
    this.dlgVars.ftrEditorValue = ``;
    this.dlgVars.ftrEditorVisible = false;
    this.computeNotificationsDialogValidation();
  }

  ftrEditorDone() {
    this.dlgVars.notification.bodyFooterText = this.dlgVars.ftrEditorValue;
    this.dlgVars.ftrEditorValue = ``;
    this.dlgVars.ftrEditorVisible = false;
    this.computeNotificationsDialogValidation();
  }

  ftrEditorShow() {
    this.dlgVars.ftrEditorValue = this.dlgVars.notification.bodyFooterText;
    this.dlgVars.ftrEditorVisible = true;
    this.computeNotificationsDialogValidation();
  }
}
